import {
  ActivityPacing,
  ActivitySettingKey,
  ActivityStage,
  ActivityStatus,
  ActivityType,
  Correctness,
  QuestionTypes,
} from '@showbie-socrative/socrative-utils';
import { schema } from 'normalizr';
import { unescape } from 'underscore';

import { QuestionScore as ApiQuestionScore } from '../../api/activities/activityApi';

interface ActivitySettingsMap {
  [ActivitySettingKey.Pacing]: ActivityPacing;
  [ActivitySettingKey.IsOpenNavigation]: boolean;
  [ActivitySettingKey.IsOneAttempt]: boolean;
  [ActivitySettingKey.IsRandomQuestions]: boolean;
  [ActivitySettingKey.IsRandomAnswers]: boolean;
}

export interface ActivitySettingsCamelCase {
  pacing?: ActivityPacing;
  requireNames?: boolean;
  randomQuestions?: boolean;
  randomAnswers?: boolean;
  showFeedback?: boolean;
  showResults?: boolean;
  allowRepeatResponses?: boolean;
  oneAttempt?: boolean;
  allowStudentNav?: boolean;
  seconds?: number;
  quizTimerSeconds?: number;
  teamCount?: number;
  teamAssignmentType?: 'auto' | 'student';
}

export interface Student {
  id: number;
  userUuid: string;
  name: string;
  isPresent?: boolean;
  isFinished: boolean;
}

export interface Room {
  id: number;
  isDefault: boolean;
  isRostered: boolean;
  nameLower: string;
  showbieClass_id: number;
  showbieClassName: string;
}

export interface Activity {
  id: number;
  quizId: number;
  startTime: string;
  endTime: string;
  activityStage: ActivityStage;
  activityState: number;
  state: ActivityStatus;
  hideReport: boolean;
  activityType: ActivityType;
  settings: ActivitySettingsMap;
  responses: string[];
  questionScores: number[];
  isRostered: boolean;
  passRate: number | null;
  totalStudents?: number | null;
  presentStudents?: number;
  completionRate?: number;
  averageScoreRate?: number | null;
  passingStudentsRate?: number | null;
}

export interface QuestionScore {
  id: number;
  questionId: number;
  activityInstanceId: number;
  gradingWeight: number;
  ungradedScore: number;
  updatedDate: Date;
  studentScores: Record<number, StudentScore>;
}

export interface StudentScore {
  id: number;
  studentId: number;
  score: number;
  correctness: Correctness;
  note: string;
  updatedDate: Date;
}

export type ApiQuestionResponse = {
  questionId: number;
  selectedAnswerIds?: Array<number>;
  isCorrect: boolean | null;
  team: number;
  hidden: boolean;
  userUuid: string;
  id: number;
  textResponse?: string;
  timestamp?: Date;
};

export type BaseActivityResponse = {
  id: number;
  isAutograded: boolean;
  isCorrect: boolean | null;
  hidden: boolean;
  questionId: number;
  userUuid: string;
  timestamp: Date;
  team: number;
};

export type ShortAnswerResponse = BaseActivityResponse & {
  type: QuestionTypes.FreeResponse;
  textResponse: string;
};

export type MultipleChoiceResponse = BaseActivityResponse & {
  type: QuestionTypes.MultipleChoice;
  selectedAnswers: number[];
};

export type TrueFalseResponse = BaseActivityResponse & {
  type: QuestionTypes.TrueFalse;
  selectedAnswer: number;
};

export function isTrueFalseResponse(
  response: StudentActivityResponse
): response is TrueFalseResponse {
  return (response as TrueFalseResponse).selectedAnswer !== undefined;
}

export function isMultipleChoiceResponse(
  response: StudentActivityResponse
): response is MultipleChoiceResponse {
  return (response as MultipleChoiceResponse).selectedAnswers !== undefined;
}

export function isShortAnswerResponse(
  response: StudentActivityResponse
): response is ShortAnswerResponse {
  return (response as ShortAnswerResponse).textResponse !== undefined;
}

export type StudentActivityResponse =
  | ShortAnswerResponse
  | MultipleChoiceResponse
  | TrueFalseResponse;

const roomSchema = new schema.Entity<Room>('room');

const responseSchema = new schema.Entity<Response>('responses');

const studentSchema = new schema.Entity<Student>(
  'students',
  {},
  {
    processStrategy: (student) => {
      return { ...student, name: unescape(student.name) };
    },
  }
);

const questionScoreSchema = new schema.Entity(
  'questionScores',
  {},
  {
    idAttribute: 'questionId',
    processStrategy: (value: ApiQuestionScore): QuestionScore => {
      return {
        id: value.id,
        activityInstanceId: value.activityInstanceId,
        updatedDate: new Date(value.updatedDate),
        questionId: value.questionId,
        gradingWeight: value.gradingWeight,
        ungradedScore: value.ungradedScore,
        studentScores: value.studentQuestionScores.reduce(
          (accumulator, current) => {
            accumulator[current.studentId] = {
              ...current,
              updatedDate: new Date(current.updatedDate),
            };
            return accumulator;
          },
          {}
        ),
      };
    },
  }
);

export const activitySchema = new schema.Entity<Activity>(
  'activities',
  {
    students: [studentSchema],
    room: roomSchema,
    responses: [responseSchema],
    questionScores: [questionScoreSchema],
  },
  {
    processStrategy: (value) => {
      const newValue = {
        ...value,
        activityStage: value.endTime
          ? ActivityStage.Finished
          : ActivityStage.InProgress,
      };
      return newValue;
    },
  }
);
